import axios, { HttpStatusCode } from 'axios';
import { getApiErrorMessages } from './apiError';

/** What a failed pre- or post-authenticate call means for the creator invite flow. */
export const InviteAuthFailure = {
  /** The magic link is missing, invalid, expired or points at nothing. */
  INVALID_INVITE: 'invalid-invite',
  /** The creator or email is already tied to something that blocks this invite. */
  CONFLICT: 'conflict',
  /** The sign-in session could not be used, so the creator must start over. */
  SESSION: 'session',
  /** The server failed or could not be reached. */
  SERVER_ERROR: 'server-error',
  /** Anything else, such as validation errors. */
  OTHER: 'other',
} as const;

export type InviteAuthFailure = (typeof InviteAuthFailure)[keyof typeof InviteAuthFailure];

function readError(error: unknown) {
  if (!axios.isAxiosError(error)) return { status: undefined, messages: [] as string[] };
  return { status: error.response?.status, messages: getApiErrorMessages(error) };
}

const isServerFailure = (status: number | undefined) =>
  status !== undefined && status >= HttpStatusCode.InternalServerError;

/** The invite JWT guard and the invite lookups word their 401s around "invite" or "authorization token". */
const INVITE_UNAUTHORIZED = /invite|authorization token/i;

/** The gateway reports a platform collision during pre-authenticate as a 400 with "Bad Input". */
const PRE_AUTH_CONFLICT_MESSAGE = 'Bad Input';

export function classifyPreAuthenticateError(error: unknown): InviteAuthFailure {
  const { status, messages } = readError(error);

  if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.NotFound) {
    return InviteAuthFailure.INVALID_INVITE;
  }
  if (status === HttpStatusCode.BadRequest && messages.includes(PRE_AUTH_CONFLICT_MESSAGE)) {
    return InviteAuthFailure.CONFLICT;
  }
  if (isServerFailure(status)) return InviteAuthFailure.SERVER_ERROR;
  return InviteAuthFailure.OTHER;
}

export function classifyPostAuthenticateError(error: unknown): InviteAuthFailure {
  const { status, messages } = readError(error);

  if (status === HttpStatusCode.NotFound) return InviteAuthFailure.INVALID_INVITE;
  if (status === HttpStatusCode.Unauthorized) {
    return messages.some((message) => INVITE_UNAUTHORIZED.test(message))
      ? InviteAuthFailure.INVALID_INVITE
      : InviteAuthFailure.SESSION;
  }
  if (status === HttpStatusCode.Conflict || status === HttpStatusCode.BadRequest) {
    return InviteAuthFailure.CONFLICT;
  }
  return InviteAuthFailure.SERVER_ERROR;
}
